import type React from 'react';

import { Spinner } from '@change/ds-core-ui/Spinner';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import { useConstituentPsfExperiment, useTreatConstituentPsfExperiment } from '#src/app/pages/psf/share/shared/hooks';
import { useConstituentStatus, useConstituentStatusPending } from '#src/app/shared/constituents';
import { useIsMobile } from '#src/app/shared/hooks/userAgent';

import { ConstituentShareFlow } from './components/ConstituentShareFlow';
import { ControlPageBody } from './components/ControlPageBody';

/**
 * Picks the body for this signer. Identified local constituents enrolled in the Constituents-in-PSF
 * experiment (CHANGE-100768) see the new local-signer goal screen in `v2`/`v4`; everyone else sees the
 * unchanged control body.
 *
 * `useTreatConstituentPsfExperiment` runs unconditionally so control constituents are enrolled and
 * exposed too (control is a treatment arm), unless the combo page's share sheet already enrolled them.
 *
 * `isConstituent` is client-only state resolved asynchronously from sessionStorage. Rather than render
 * control first and swap to the variant once it resolves (a jarring flash), we hold a neutral
 * placeholder during the pending window — but only for the variant-eligible bucket (mobile + a real
 * `v2`/`v4` assignment). Everyone else (desktop, control, fallback) renders control immediately with no
 * placeholder, so the hold never affects the common path. SSR and the first client render both see the
 * placeholder for that bucket, so there is no hydration mismatch.
 */
export const PageBody: React.FC<EmptyProps> = () => {
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePsfSharePageData();
	const [constituentStatus] = useConstituentStatus(petitionId);
	const statusPending = useConstituentStatusPending(petitionId);
	const isMobile = useIsMobile();
	const { experiment, variation } = useConstituentPsfExperiment();

	useTreatConstituentPsfExperiment();

	const isVariantArm =
		isMobile && !experiment.fallback && (variation === 'v2_new_share' || variation === 'v4_new_goal');

	// Not in the variant-eligible bucket: control is the answer regardless of constituent status.
	if (!isVariantArm) {
		return <ControlPageBody />;
	}

	// Variant-eligible, but we don't yet know if this signer is a constituent: hold instead of
	// flashing control, since a resolved constituent would otherwise replace it with a different screen.
	if (statusPending) {
		return (
			<div className="flex min-h-[817px] items-center justify-center" data-qa="constituent-flow-loading">
				<Spinner />
			</div>
		);
	}

	if (constituentStatus?.isConstituent) {
		return <ConstituentShareFlow />;
	}

	return <ControlPageBody />;
};
